// Capturify's timeline hands out S3 URLs presigned for one hour. Reports store
// image URLs verbatim, so those links go blank (403) once they expire. Capturify
// serves stable links that redirect to a freshly signed URL; convert to those
// before storing or rendering anything.
const CAPTURIFY_PUBLIC_API = 'https://api.capturify.chimneysweeps.com/api/public';

const BUCKET_ROUTES: Record<string, string> = {
  capturify: 'photo',
  'company-cam-backup': 'backup-photo',
};

// https://<bucket>.s3[.<region>].amazonaws.com/<key> or https://s3[.<region>].amazonaws.com/<bucket>/<key>
const VIRTUAL_HOSTED = /^([a-z0-9.-]+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
const PATH_STYLE = /^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;

export const toStableCapturifyUrl = (url: string): string => {
  if (typeof url !== 'string' || !url.includes('amazonaws.com')) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  let bucket: string | undefined;
  let keyPath = parsed.pathname.slice(1);
  const hosted = parsed.hostname.match(VIRTUAL_HOSTED);
  if (hosted) {
    bucket = hosted[1];
  } else if (PATH_STYLE.test(parsed.hostname)) {
    const slash = keyPath.indexOf('/');
    if (slash < 0) return url;
    bucket = keyPath.slice(0, slash);
    keyPath = keyPath.slice(slash + 1);
  }

  const route = bucket && BUCKET_ROUTES[bucket];
  if (!route || !keyPath) return url;

  const encodedKey = keyPath
    .split('/')
    .map(segment => encodeURIComponent(decodeURIComponent(segment)))
    .join('/');
  return `${CAPTURIFY_PUBLIC_API}/${route}/${encodedKey}`;
};

// Deep-rewrites every expiring Capturify URL inside saved report data.
export const stabilizeCapturifyUrls = <T,>(value: T): T => {
  if (typeof value === 'string') return toStableCapturifyUrl(value) as unknown as T;
  if (Array.isArray(value)) return value.map(item => stabilizeCapturifyUrls(item)) as unknown as T;
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      result[key] = stabilizeCapturifyUrls(item);
    });
    return result as T;
  }
  return value;
};
